import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Spin, Typography } from "antd";
import axios from "axios";
import moment from "moment";

const formatCurrency = (n) =>
	`₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Client-side fallback if API has no stocks_by_location (older backend). */
function aggregateEntriesByLocation(productStockRows, salePrice) {
	if (!Array.isArray(productStockRows) || productStockRows.length === 0) return [];
	const byLoc = new Map();
	for (const e of productStockRows) {
		const locId = e.location_id != null ? e.location_id : e.location?.id ?? null;
		const key = locId != null ? String(locId) : "_no_location";
		if (!byLoc.has(key)) {
			byLoc.set(key, {
				location_id: locId,
				location_name: e.location?.name ?? "—",
				quantity: 0,
				last_list_price: null,
			});
		}
		const row = byLoc.get(key);
		if (e.location?.name) row.location_name = e.location.name;
		const q = Number(e.quantity) || 0;
		row.quantity += q;
		if (e.list_price != null) row.last_list_price = Number(e.list_price);
	}
	return Array.from(byLoc.values()).map((row) => ({
		location_id: row.location_id,
		location_name: row.location_name,
		quantity: row.quantity,
		sale_price: salePrice != null ? Number(salePrice) : null,
		list_price: row.last_list_price,
	}));
}

const StockViewModal = ({ productId, productName, visible, onClose }) => {
	const [loading, setLoading] = useState(false);
	const [product, setProduct] = useState(null);

	useEffect(() => {
		if (!visible || !productId) {
			setProduct(null);
			return;
		}
		setLoading(true);
		axios
			.get(`product/${productId}`)
			.then((res) => {
				setProduct(res.data?.data ?? res.data);
			})
			.catch(() => setProduct(null))
			.finally(() => setLoading(false));
	}, [visible, productId]);

	const stockRows = product?.product_stock || [];
	const totalQuantityFromRows = stockRows.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0);
	const displayTotal = stockRows.length > 0 ? totalQuantityFromRows : (product?.quantity ?? 0);

	const stocksByLocation = useMemo(() => {
		if (product?.stocks_by_location?.length) return product.stocks_by_location;
		return aggregateEntriesByLocation(
			stockRows,
			product?.sale_price
		);
	}, [product, stockRows]);

	const summaryColumns = [
		{
			title: "Location",
			key: "location",
			render: (_, row) => row.location_name ?? "—",
		},
		{
			title: "Qty",
			dataIndex: "quantity",
			key: "quantity",
			align: "right",
		},
		{
			title: "Sale price",
			key: "sale_price",
			align: "right",
			render: (_, row) =>
				row.sale_price != null ? formatCurrency(row.sale_price) : "—",
		},
		{
			title: "List price",
			key: "list_price",
			align: "right",
			render: (_, row) => (row.list_price != null ? formatCurrency(row.list_price) : "—"),
		},
	];

	const columns = [
		{
			title: "Location",
			key: "location",
			dataIndex: ["location", "name"],
			render: (_, row) => row.location?.name ?? "—",
		},
		{
			title: "Transaction Date",
			dataIndex: "transactionDate",
			key: "transactionDate",
			render: (d) => (d ? moment(d).format("DD MMM YYYY") : "—"),
		},
		{
			title: "List Price",
			dataIndex: "list_price",
			key: "list_price",
			align: "right",
			render: (v) => formatCurrency(v),
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			align: "right",
		},
	];

	return (
		<Modal
			title={productName ? `Stock — ${productName}` : "Stock"}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={780}
			destroyOnClose
		>
			{loading ? (
				<div className="text-center py-4">
					<Spin size="large" />
				</div>
			) : (
				<>
					{product && (
						<div className="mb-3">
							<strong>Total quantity (product):</strong> {displayTotal}
						</div>
					)}
					{stocksByLocation.length > 0 && (
						<div className="mb-4">
							<Typography.Text strong className="d-block mb-2">
								By location (qty &amp; price)
							</Typography.Text>
							<Table
								size="small"
								dataSource={stocksByLocation}
								columns={summaryColumns}
								rowKey={(r) => r.location_id ?? r.location_name}
								pagination={false}
								locale={{ emptyText: "No locations" }}
							/>
						</div>
					)}
					{stockRows.length > 0 && (
						<Typography.Text strong className="d-block mb-2">
							Stock entries (detail)
						</Typography.Text>
					)}
					<Table
						dataSource={stockRows}
						columns={columns}
						rowKey="id"
						pagination={false}
						locale={{ emptyText: "No stock entries" }}
					/>
				</>
			)}
		</Modal>
	);
};

export default StockViewModal;
